import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Box, InputAdornment, TableRow, IconButton, Button } from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import AlertPopup from "../../../components/common/AlertPopup";
import { selectUserProfile } from "../../../modules/user/selectors";

import TotalIcon from '../../../assets/facility/total-icon.svg'
import NewRegistratonIcon from '../../../assets/facility/new-registration.svg'
import SuspendedIcon from '../../../assets/facility/suspended-icon.svg'

import {
  SummaryCardsContainer,
  SummaryCard,
  SummaryIconWrapper,
  SummaryContent,
  SummaryTitle,
  SummaryValue,
  SummaryDescription,
  FacilityListContainer,
  PageHeader,
  PageTitle,
  Filters,
  SearchInput,
  StatusTab,
  StatusTabsContainer,
  StyledTabs,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  PaginationFooter,
  StatusTag
} from "./styles";

import { useSidebar } from '../../../context/SidebarContext';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';

import PaginatedSearchSelect from '../../../components/PaginatedSearchSelect';
import SortDropdown, { SortOption } from '../../../components/SortDropdown';
import { FetchResponse } from '@/interfaces/paginatedSearchSelect.interface';
import { AppDispatch } from '../../../store';

import {
  fetchFacilityStatistics,
  selectFacilityStatistics,
  selectFacilityStatisticsLoading,
  IFacility,
  selectFacilityList,
  selectFacilityListLoading,
  selectFacilityListError,
  selectFacilityListCount,
  fetchFacilityList,
  fetchFacilityLocations,
  IFacilityLocation
} from '../../../modules/facility';

import CustomTableHeader from "../../../components/CustomHeader";
import { useNavigate } from "react-router-dom";
import CustomPagination from "../../../components/CustomPagination";
import Loader from '../../../components/common/Loader';

const PRODUCT_SORT_OPTIONS: SortOption[] = [
  { id: 'facility_id-desc', label: 'ID No (Z - A)' },
  { id: 'facility_id-asc', label: 'ID No (A - Z)' },
  { id: 'facility_name-asc', label: 'Name (A - Z)' },
  { id: 'facility_name-desc', label: 'Name (Z - A)' }
];

type Order = 'asc' | 'desc';

interface Column {
  id: keyof IFacility | string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const COLUMN_MAPPING: { [key: string]: keyof IFacility | string } = {
  'ID No': 'facility_id',
  'Name': 'facility_name',
  'Type': 'facility_type.facility_type_name',
  'Address': 'address',
  'Contact Person': 'contact_person',
  'Designation': 'contact_person_designation',
  'Status': 'status'
};

// Define the columns structure needed by CustomTableHeader
const FACILITY_COLUMNS: Column[] = Object.entries(COLUMN_MAPPING).map(([label, id]) => ({
  id: id,
  label: label,
  numeric: id === 'facility_id',
  disableSorting: true,
}));

const FacilitiesListPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  const userProfile = useSelector(selectUserProfile);
  const orgCode = userProfile?.data?.org_code;
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  const handleCopyInviteLink = () => {
    if (orgCode) {
      const inviteUrl = `${window.location.origin}/facility/signup?org_code=${orgCode}`;
      navigator.clipboard.writeText(inviteUrl);
      setAlert({ message: 'Facility invite link copied to clipboard!', severity: 'success' });
    }
  };
  const [selectedFacilityLocation, setSelectedFacilityLocation] = useState<IFacilityLocation | null>(null);
  const [currentSort, setCurrentSort] = useState(PRODUCT_SORT_OPTIONS[0].id);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [status, setStatus] = useState<string>('All');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof IFacility | string>('facility_id');

  const facilityStatistics = useSelector(selectFacilityStatistics);
  const facilityStatisticsLoading = useSelector(selectFacilityStatisticsLoading);
  const facilityList = useSelector(selectFacilityList);
  const facilityListLoading = useSelector(selectFacilityListLoading);
  const facilityListError = useSelector(selectFacilityListError);
  const facilityListCount = useSelector(selectFacilityListCount);

  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();

  useEffect(() => {
    setActivePage('facilities');
    setPath([
      { title: 'Management', path: '/' },
      { title: 'Facility', path: '/' },
    ]);
    setIsShow(false);
  }, [setActivePage, setPath, setIsShow]);

  useEffect(() => {
    dispatch(fetchFacilityStatistics());
  }, [dispatch]);

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();
    if (trimmedSearchTerm.length > 0 && trimmedSearchTerm.length < 3) {
      return;
    }
    dispatch(fetchFacilityList({
      page,
      pageSize: rowsPerPage,
      search: trimmedSearchTerm,
      locationName: selectedFacilityLocation?.location_name,
      orderBy: orderBy as string,
      order: order,
      status: status
    }));
  }, [
    dispatch,
    page,
    rowsPerPage,
    searchTerm,
    status,
    selectedFacilityLocation,
    orderBy,
    order
  ]);

  const fetchFacilityLocationOptionsCallback = useCallback(async (search: string, page: number): Promise<FetchResponse<IFacilityLocation>> => {
    const response = await dispatch(fetchFacilityLocations({ page, pageSize: 15, search }));
    if (fetchFacilityLocations.fulfilled.match(response)) {
      const payload = response.payload as any;
      // The API returns the array directly in 'data' based on your snippet
      const rawOptions = Array.isArray(payload) ? payload : (payload.results || []);
      const totalCount = payload.count || (Array.isArray(payload) ? payload.length : 0);

      // Process options: filter nulls, ensure unique stable IDs
      const processedOptions = rawOptions
        .filter((loc: any) => loc && loc.location_name)
        .map((loc: any) => ({
          ...loc,
          // Generate a stable ID if none exists to ensure consistency across re-renders/paging
          id: loc.id || `${loc.location_name}-${loc.latitude}-${loc.longitude}`.replace(/\s+/g, '-'),
        }));

      // Deduplicate options based on the generated ID
      const uniqueOptions = Array.from(new Map(processedOptions.map((item: any) => [item.id, item])).values()) as IFacilityLocation[];

      return {
        options: uniqueOptions,
        // If the API returns everything at once (list), hasMore should be false
        hasMore: Array.isArray(payload) ? false : (page * 15) < totalCount,
      };
    } else {
      return {
        options: [],
        hasMore: false,
      };
    }
  }, [dispatch]);

  const handleSortChange = (newSortId: string) => {
    setCurrentSort(newSortId);
    const [newOrderBy, newOrder] = newSortId.split('-');
    setOrderBy(newOrderBy);
    setOrder(newOrder as Order);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    setPage(1);
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const propertyKey = property as keyof IFacility | string;

    const columnDef = FACILITY_COLUMNS.find(col => col.id === property);
    if (columnDef?.disableSorting) {
      return;
    }

    const isAsc = orderBy === propertyKey && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(propertyKey);
  };

  return (
    <Box>
      {facilityListLoading && <Loader open={facilityListLoading} />}
      <SummaryCardsContainer>
        <SummaryCard>
          <SummaryIconWrapper colorName="primary">
            <img src={TotalIcon} alt="Total Facilities" />
          </SummaryIconWrapper>
          <SummaryContent>
            <SummaryTitle>Total No of Facilities</SummaryTitle>
            <SummaryValue>{facilityStatisticsLoading ? '0' : facilityStatistics?.total_facilities ?? 0}</SummaryValue>
            <SummaryDescription>{facilityStatisticsLoading ? '0' : `${facilityStatistics?.new_facilities_last_30_days ?? 0} New in last 30 days`}</SummaryDescription>
          </SummaryContent>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrapper colorName="info">
            <img src={NewRegistratonIcon} alt="New Registration Facilities" />
          </SummaryIconWrapper>
          <SummaryContent>
            <SummaryTitle>New Registration</SummaryTitle>
            <SummaryValue>{facilityStatisticsLoading ? '0' : facilityStatistics?.new_facilities_last_30_days ?? 0}</SummaryValue>
            <SummaryDescription>Last 30 Days</SummaryDescription>
          </SummaryContent>
        </SummaryCard>
        <SummaryCard>
          <SummaryIconWrapper colorName="error">
            <img src={SuspendedIcon} alt="Suspended Facilities" />
          </SummaryIconWrapper>
          <SummaryContent>
            <SummaryTitle>Suspended Facilities</SummaryTitle>
            <SummaryValue>{facilityStatisticsLoading ? '0' : facilityStatistics?.suspended_facilities ?? 0}</SummaryValue>
            <SummaryDescription>Require Action</SummaryDescription>
          </SummaryContent>
        </SummaryCard>
      </SummaryCardsContainer>


      <FacilityListContainer>
        {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
        <PageHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PageTitle>Facility List</PageTitle>
            {orgCode && (
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<LinkIcon />} 
                onClick={handleCopyInviteLink}
                sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                    px: 2,
                    height: '36px',
                    borderColor: '#5E8E7E',
                    color: '#5E8E7E',
                    '&:hover': {
                        borderColor: '#5E8E7E',
                        backgroundColor: 'rgba(94, 142, 126, 0.08)'
                    }
                }}
              >
                Copy Invite Link
              </Button>
            )}
          </Box>
          <Filters>
            <SearchInput
              placeholder="Search by Name, ID..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value as string)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton
                        aria-label="clear search"
                        onClick={() => setSearchTerm('')}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <PaginatedSearchSelect
              fetchOptions={fetchFacilityLocationOptionsCallback}
              getOptionLabel="location_name"
              label="Facility Location"
              value={selectedFacilityLocation}
              onChange={(newValue) => setSelectedFacilityLocation(newValue as IFacilityLocation | null)}
              placeholder="Select Facility Location"
            />
            <SortDropdown
              options={PRODUCT_SORT_OPTIONS}
              activeSortId={currentSort}
              onSortChange={handleSortChange}
            />
          </Filters>
        </PageHeader>

        <StatusTabsContainer>
          <StyledTabs value={tabValue} onChange={handleTabChange} aria-label="status tabs">
            <StatusTab
              label={"All (" + facilityStatistics?.total_facilities + ")"}
              onClick={() => handleStatusChange('')}
            />
            <StatusTab
              label={"Pending (" + facilityStatistics?.pending_facilities + ")"}
              onClick={() => handleStatusChange('pending')}
            />
            <StatusTab
              label={"Suspended (" + facilityStatistics?.suspended_facilities + ")"}
              onClick={() => handleStatusChange('suspended')}
            />
            <StatusTab
              label={"Approved (" + facilityStatistics?.approved_facilities + ")"}
              onClick={() => handleStatusChange('approved')}
            />
          </StyledTabs>
        </StatusTabsContainer>

        <StyledTableContainer>
          <StyledTable stickyHeader>
            <CustomTableHeader
              columns={FACILITY_COLUMNS}
              order={order}
              orderBy={orderBy as string}
              onRequestSort={handleRequestSort}
            />

            <StyledTableBody>
              {facilityListLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_COLUMNS.length} align="center">
                    {/* <Loader open={facilityListLoading} /> */}
                  </StyledTableCell>
                </TableRow>
              ) : facilityListError || !facilityList?.length ? (
                <TableRow>
                  <StyledTableCell colSpan={FACILITY_COLUMNS.length} align="center">
                    No data available
                  </StyledTableCell>
                </TableRow>
              ) : (
                facilityList.map((facility) => (
                  <TableRow
                    key={facility.facility_id}
                    hover
                    onClick={() => {
                      let path = (facility.status === 'approved') ? 'details' : 'review';
                      navigate(`/facility/${facility.facility_id}/${path}`)
                    }
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <StyledTableCell>{facility.facility_id}</StyledTableCell>
                    <StyledTableCell>{facility.facility_name}</StyledTableCell>
                    <StyledTableCell>{facility.facility_type_name}</StyledTableCell>
                    <StyledTableCell>{facility.address}</StyledTableCell>
                    <StyledTableCell>{facility.contact_person}</StyledTableCell>
                    <StyledTableCell>{facility.contact_person_designation}</StyledTableCell>
                    <StyledTableCell>
                      <StatusTag status={facility.status}>
                        {facility.status}
                      </StatusTag>
                    </StyledTableCell>
                  </TableRow>
                ))
              )}
            </StyledTableBody>
          </StyledTable>
        </StyledTableContainer>
      </FacilityListContainer>
      <PaginationFooter>
        <Box>
          Showing {facilityList?.length || 0} of {facilityListCount} results
        </Box>
        <CustomPagination
          totalPages={Math.ceil(facilityListCount / rowsPerPage)}
          currentPage={page}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </PaginationFooter>
    </Box>
  )
}

export default FacilitiesListPage;